import { useState, useMemo, useCallback } from "react";
import { Input, Checkbox } from "@legal-500/legal500-design-system";
import type { Entity } from "./EntityCard";
import { EntityCard } from "./EntityCard";
import styles from "./EntityPicker.module.css";

export type EntityPickerColumns = {
  mobile?: number;
  tablet?: number;
  desktop?: number;
};

export type EntityPickerProps = {
  entities: Entity[];
  selectedValues: (number | string)[];
  onChange: (selectedValues: (number | string)[]) => void;
  maxSelections?: number;
  minSelections?: number;
  error?: string;
  disabled?: boolean;
  columns?: EntityPickerColumns;
  compact?: boolean;
  label?: string;
  helperText?: string;
  emptyStateNoun?: string;
  // Group selection support (for practice groups)
  enableGroupSelection?: boolean;
  selectedGroups?: (number | string)[];
  onGroupSelectionChange?: (
    selectedGroups: (number | string)[],
    selectedValues: (number | string)[],
  ) => void;
  // Map of group name to group ID (needed to track which group is selected)
  groupIdMap?: Map<string, number | string>;
};

function formatGroupName(groupName: string): string {
  return groupName
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function EntityPicker({
  entities,
  selectedValues,
  onChange,
  maxSelections,
  minSelections,
  error,
  disabled = false,
  columns,
  compact = false,
  label,
  helperText,
  emptyStateNoun,
  enableGroupSelection = false,
  selectedGroups = [],
  onGroupSelectionChange,
  groupIdMap,
}: EntityPickerProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredEntities = useMemo(() => {
    if (!searchQuery.trim()) {
      return entities;
    }

    const query = searchQuery.toLowerCase();
    return entities.filter(
      entity =>
        entity.label.toLowerCase().includes(query) ||
        entity.description?.toLowerCase().includes(query),
    );
  }, [entities, searchQuery]);

  // Group entities by their group field
  const groupedEntities = useMemo(() => {
    const groups = new Map<string | undefined, Entity[]>();

    filteredEntities.forEach(entity => {
      const groupName = entity.group || undefined;
      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)!.push(entity);
    });

    // Sort groups alphabetically, with undefined last
    return Array.from(groups.entries()).sort((a, b) => {
      if (a[0] === undefined) return 1;
      if (b[0] === undefined) return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [filteredEntities]);

  function handleEntityToggle(entityValue: number | string) {
    if (disabled) return;

    const isSelected = selectedValues.includes(entityValue);

    if (isSelected) {
      // Deselect
      const newSelection = selectedValues.filter(val => val !== entityValue);
      onChange(newSelection);
    } else {
      // Select - check max limit
      if (maxSelections && selectedValues.length >= maxSelections) {
        // Don't allow more selections
        return;
      }
      const newSelection = [...selectedValues, entityValue];
      onChange(newSelection);
    }
  }

  const handleGroupToggle = useCallback(
    (groupName: string, groupEntities: Entity[]) => {
      if (disabled || !enableGroupSelection || !onGroupSelectionChange || !groupIdMap) return;

      const groupId = groupIdMap.get(groupName);
      if (groupId === undefined) return;

      const normalizedGroupId = Number(groupId);
      const isCurrentlySelected = selectedGroups.some(g => Number(g) === normalizedGroupId);
      const groupEntityValues = groupEntities.map(e => e.value);

      if (isCurrentlySelected) {
        const newGroups = selectedGroups.filter(g => Number(g) !== normalizedGroupId);
        onGroupSelectionChange(newGroups, selectedValues);
      } else {
        const newGroups = [...selectedGroups, normalizedGroupId];
        const newValues = selectedValues.filter(v => !groupEntityValues.includes(v));
        onGroupSelectionChange(newGroups, newValues);
      }
    },
    [
      disabled,
      enableGroupSelection,
      onGroupSelectionChange,
      groupIdMap,
      selectedGroups,
      selectedValues,
    ],
  );

  const isGroupSelected = useCallback(
    (groupName: string): boolean => {
      if (!enableGroupSelection || !groupIdMap) return false;
      const groupId = groupIdMap.get(groupName);
      if (groupId === undefined) return false;
      const normalizedGroupId = Number(groupId);
      return selectedGroups.some(g => Number(g) === normalizedGroupId);
    },
    [enableGroupSelection, groupIdMap, selectedGroups],
  );

  if (entities.length === 0) {
    const displayNoun = emptyStateNoun || (label ? label.toLowerCase() : "entities");
    return (
      <div className={styles.container}>
        {label && (
          <div className={styles.labelWrapper}>
            <label className={styles.label}>{label}</label>
            {helperText && <span className={styles.helperText}>{helperText}</span>}
          </div>
        )}
        <div className={styles.emptyState}>No {displayNoun} found for this company</div>
      </div>
    );
  }

  // Count both individual selections and group selections
  const selectedCount = selectedValues.length + selectedGroups.length;
  const hasMaxLimit = maxSelections !== undefined;
  const limitReached = hasMaxLimit && selectedCount >= maxSelections;
  const effectiveMin = minSelections ?? maxSelections ?? 0;
  const hasValidSelection =
    selectedCount >= effectiveMin &&
    (maxSelections === undefined || selectedCount <= maxSelections);

  const gridStyle = {
    "--columns-mobile": columns?.mobile ?? 1,
    "--columns-tablet": columns?.tablet ?? 2,
    "--columns-desktop": columns?.desktop ?? 2,
  } as React.CSSProperties;

  return (
    <div className={`${styles.container} ${compact ? styles.containerCompact : ""}`}>
      <div className={styles.headerRow}>
        {label && <label className={styles.label}>{label}</label>}
        <div className={styles.selectedInfoInline}>
          <span className={hasValidSelection ? styles.successText : styles.warningText}>
            {selectedCount}
            {hasMaxLimit && `/${maxSelections}`} selected
          </span>
          <span className={styles.separator}>•</span>
          <span className={styles.resultsCount} aria-live="polite" aria-atomic="true">
            {filteredEntities.length} available
          </span>
          {limitReached && <span className={styles.limitText}>(max)</span>}
        </div>
      </div>

      {helperText && <span className={styles.helperText}>{helperText}</span>}

      <div className={styles.searchBarWrapper}>
        <Input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search..."
          fullWidth
        />
      </div>

      {enableGroupSelection && (
        <div className={styles.groupSelectionHint}>
          Tip: Check the box next to a group name to select all areas in that group
        </div>
      )}

      <div className={styles.scrollableGrid}>
        {filteredEntities.length === 0 ? (
          <div className={styles.noResults}>No entities match "{searchQuery}"</div>
        ) : (
          groupedEntities.map(([groupName, groupEntities]) => {
            const groupSelected = groupName ? isGroupSelected(groupName) : false;
            return (
              <div key={groupName ?? "ungrouped"} className={styles.groupSection}>
                {groupName && (
                  <div className={styles.groupHeader}>
                    {enableGroupSelection && groupIdMap?.has(groupName) && (
                      <Checkbox
                        checked={groupSelected}
                        onChange={() => handleGroupToggle(groupName, groupEntities)}
                        disabled={disabled}
                      />
                    )}
                    <h3 className={styles.groupTitle}>{formatGroupName(groupName)}</h3>
                    {groupSelected && <span className={styles.allAreasBadge}>All areas</span>}
                  </div>
                )}
                <div
                  className={`${styles.entityGrid} ${groupSelected ? styles.groupSelectedGrid : ""}`}
                  style={gridStyle}
                >
                  {groupEntities.map(entity => {
                    const isSelected = groupSelected || selectedValues.includes(entity.value);
                    const isDisabled = disabled || groupSelected || (!isSelected && limitReached);

                    return (
                      <EntityCard
                        key={entity.value}
                        entity={entity}
                        isSelected={isSelected}
                        onToggle={handleEntityToggle}
                        disabled={isDisabled}
                        compact={compact}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>

      {error && <div className={styles.validationError}>{error}</div>}
    </div>
  );
}

export type { Entity } from "./EntityCard";
